import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Grid, Stack, Typography, CircularProgress,
  Divider, Snackbar, Alert,
} from '@mui/material';
import AddRoundedIcon         from '@mui/icons-material/AddRounded';
import LinkOffRoundedIcon     from '@mui/icons-material/LinkOffRounded';
import EditRoundedIcon        from '@mui/icons-material/EditRounded';
import SaveRoundedIcon        from '@mui/icons-material/SaveRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext }  from '../../components/signin/AuthContext';
import { useGrowSetup } from '../../GrowSetupContext';
import DeviceWizard     from '../components/DeviceWizard';
import { useBasePath }  from '../theme';

// ── Keyframes ────────────────────────────────────────────────────────────────

const ANIM_CSS = `
@keyframes v4FadeUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
@keyframes v4Pulse  { 0%,100%{box-shadow:0 0 0 0 color-mix(in srgb, var(--v4-green) 53%, transparent)} 50%{box-shadow:0 0 0 8px color-mix(in srgb, var(--v4-green) 0%, transparent)} }
@keyframes v4Blink  { 0%,100%{opacity:1} 50%{opacity:0.3} }
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5, mt: 1 }}>
      <Typography sx={{
        fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '0.16em', color: 'rgba(var(--v4-cyan-rgb),0.55)', whiteSpace: 'nowrap',
      }}>
        {children}
      </Typography>
      <Divider sx={{ flex: 1, borderColor: 'rgba(var(--v4-cyan-rgb),0.08)' }} />
    </Stack>
  );
}

function GlassCard({ children, sx = {}, ...rest }) {
  return (
    <Box sx={{
      background: 'rgba(var(--v4-surface-rgb),0.92)',
      border: '1px solid rgba(var(--v4-cyan-rgb),0.1)',
      borderRadius: '20px',
      backdropFilter: 'blur(24px)',
      p: 3,
      ...sx,
    }} {...rest}>
      {children}
    </Box>
  );
}

// ── Device definitions (static meta) ────────────────────────────────────────

const DEVICE_META = {
  klima: { label: 'Klima-Kontroller', icon: '🌡️', color: 'var(--v4-cyan)', desc: 'Temperatur, Luftfeuchte, CO₂' },
  hydro: { label: 'Hydro-Kontroller', icon: '💧', color: 'var(--v4-blue)', desc: 'pH, EC, Bewässerung' },
};

// ── Single device card ───────────────────────────────────────────────────────

function DeviceCard({ metaKey, attachedDevice, onAdd, onRemove, animDelay }) {
  const meta    = DEVICE_META[metaKey];
  const { color } = meta;
  const attached  = Boolean(attachedDevice);
  const attachedId = attachedDevice?.device_id;
  const attachedName = attachedDevice?.device_name;
  const [hover, setHover] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        position: 'relative', overflow: 'hidden',
        background: 'rgba(var(--v4-surface-rgb),0.92)',
        border: `1px solid ${attached ? `color-mix(in srgb, ${color} 27%, transparent)` : 'rgba(var(--v4-fg-rgb),0.07)'}`,
        borderRadius: '20px', backdropFilter: 'blur(24px)',
        transition: 'all 0.25s cubic-bezier(0.22,1,0.36,1)',
        boxShadow: attached ? `0 4px 28px color-mix(in srgb, ${color} 8%, transparent)` : 'none',
        animation: `v4FadeUp 0.5s ${animDelay}ms both`,
        ...(hover ? {
          border: `1px solid ${attached ? `color-mix(in srgb, ${color} 40%, transparent)` : 'rgba(var(--v4-cyan-rgb),0.18)'}`,
          boxShadow: `0 8px 40px color-mix(in srgb, ${color} 9%, transparent)`,
          transform: 'translateY(-2px)',
        } : {}),
      }}
    >
      {/* Background glow blob when attached */}
      {attached && (
        <Box sx={{
          position: 'absolute', top: -60, right: -60,
          width: 200, height: 200, borderRadius: '50%',
          background: `color-mix(in srgb, ${color} 6%, transparent)`, filter: 'blur(40px)',
          pointerEvents: 'none',
        }} />
      )}

      {/* Content */}
      <Box sx={{ position: 'relative', p: 2.5 }}>
        {/* Header */}
        <Stack direction="row" alignItems="flex-start" spacing={1.75} sx={{ mb: 2 }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
            background: `color-mix(in srgb, ${color} 8%, transparent)`, border: `1px solid ${color}${attached ? '55' : '28'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
          }}>
            {meta.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--v4-text)', fontSize: '0.95rem', lineHeight: 1.2 }}>
              {meta.label}
            </Typography>
            <Typography sx={{ fontSize: '0.67rem', color: `color-mix(in srgb, ${color} 47%, transparent)`, mt: 0.3 }}>
              {meta.desc}
            </Typography>
            {/* Status indicator */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                background: attached ? 'var(--v4-green)' : 'rgba(var(--v4-fg-rgb),0.18)',
                boxShadow: attached ? '0 0 6px var(--v4-green)' : 'none',
                animation: attached ? 'v4Pulse 2s infinite' : 'none',
                flexShrink: 0,
              }} />
              <Typography sx={{
                fontSize: '0.62rem', fontWeight: 700,
                letterSpacing: '0.08em',
                color: attached ? 'var(--v4-green)' : 'rgba(var(--v4-muted-rgb),0.25)',
              }}>
                {attached ? 'Verbunden' : 'Nicht verbunden'}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* Device name / ID */}
        {attached && (
          <Box sx={{
            px: 1.5, py: 1, borderRadius: '10px', mb: 2,
            background: `color-mix(in srgb, ${color} 3%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 9%, transparent)`,
            animation: 'v4FadeUp 0.35s both',
          }}>
            {attachedName && (
              <Typography sx={{
                fontSize: '0.82rem', color: '#e0ecff', fontWeight: 700, mb: 0.2,
              }}>
                {attachedName}
              </Typography>
            )}
            <Typography sx={{
              fontFamily: 'monospace', fontSize: '0.7rem',
              color: color, fontWeight: 700, letterSpacing: '0.05em',
            }}>
              ID: {attachedId}
            </Typography>
          </Box>
        )}

        {!attached && (
          <Typography sx={{
            fontSize: '0.72rem', color: 'rgba(var(--v4-muted-rgb),0.25)',
            lineHeight: 1.5, mb: 2,
          }}>
            Kein Gerät zugeordnet. Füge jetzt ein Gerät über die Seriennummer hinzu.
          </Typography>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          {!attached ? (
            <Box
              onClick={onAdd}
              sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 0.75, py: 1.1, borderRadius: '12px', cursor: 'pointer',
                background: `color-mix(in srgb, ${color} 8%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 27%, transparent)`,
                color, fontWeight: 700, fontSize: '0.78rem',
                transition: 'all 0.18s',
                '&:hover': { background: `color-mix(in srgb, ${color} 14%, transparent)`, boxShadow: `0 4px 20px color-mix(in srgb, ${color} 20%, transparent)` },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 16 }} /> Gerät hinzufügen
            </Box>
          ) : (
            <>
              <Box
                onClick={onAdd}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 0.5, py: 1, borderRadius: '12px', cursor: 'pointer',
                  background: 'rgba(var(--v4-fg-rgb),0.04)', border: '1px solid rgba(var(--v4-fg-rgb),0.08)',
                  color: 'rgba(var(--v4-muted-rgb),0.4)', fontWeight: 700, fontSize: '0.73rem',
                  transition: 'all 0.18s',
                  '&:hover': { background: 'rgba(var(--v4-fg-rgb),0.08)', color: 'var(--v4-text)' },
                }}
              >
                <EditRoundedIcon sx={{ fontSize: 14 }} /> Ersetzen
              </Box>
              <Box
                onClick={onRemove}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 0.5, py: 1, borderRadius: '12px', cursor: 'pointer',
                  background: 'rgba(var(--v4-red-rgb),0.07)', border: '1px solid rgba(var(--v4-red-rgb),0.25)',
                  color: 'var(--v4-red)', fontWeight: 700, fontSize: '0.73rem',
                  transition: 'all 0.18s',
                  '&:hover': { background: 'rgba(var(--v4-red-rgb),0.15)' },
                }}
              >
                <LinkOffRoundedIcon sx={{ fontSize: 14 }} /> Trennen
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

// ── Delete grow-setup confirm ────────────────────────────────────────────────

function DeleteConfirm({ open, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1500,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(12px)', background: 'rgba(var(--v4-surface2-rgb),0.65)',
      animation: 'v4FadeUp 0.2s both',
    }}>
      <Box sx={{
        background: 'rgba(var(--v4-surface2-rgb),0.97)',
        border: '1px solid rgba(var(--v4-red-rgb),0.3)',
        borderRadius: '20px', backdropFilter: 'blur(32px)',
        p: 3.5, maxWidth: 380, width: '90vw',
        boxShadow: '0 20px 60px rgba(var(--v4-red-rgb),0.15)',
        animation: 'v4FadeUp 0.3s both',
      }}>
        <Typography sx={{ fontSize: 28, textAlign: 'center', mb: 1 }}>⚠️</Typography>
        <Typography sx={{ fontWeight: 800, color: 'var(--v4-text)', fontSize: '1rem', textAlign: 'center', mb: 1 }}>
          Grow Setup löschen?
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(var(--v4-muted-rgb),0.4)', textAlign: 'center', mb: 3, lineHeight: 1.6 }}>
          Alle Gerätezuordnungen und Konfigurationen werden unwiderruflich entfernt.
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Box onClick={onCancel} sx={{
            flex: 1, py: 1.25, borderRadius: '12px', textAlign: 'center', cursor: 'pointer',
            background: 'rgba(var(--v4-fg-rgb),0.05)', border: '1px solid rgba(var(--v4-fg-rgb),0.1)',
            color: 'rgba(var(--v4-muted-rgb),0.5)', fontWeight: 700, fontSize: '0.82rem',
            '&:hover': { color: 'var(--v4-text)', background: 'rgba(var(--v4-fg-rgb),0.09)' },
          }}>
            Abbrechen
          </Box>
          <Box onClick={onConfirm} sx={{
            flex: 1, py: 1.25, borderRadius: '12px', textAlign: 'center', cursor: 'pointer',
            background: 'rgba(var(--v4-red-rgb),0.15)', border: '1px solid rgba(var(--v4-red-rgb),0.4)',
            color: 'var(--v4-red)', fontWeight: 800, fontSize: '0.82rem',
            '&:hover': { background: 'rgba(var(--v4-red-rgb),0.25)' },
          }}>
            Endgültig löschen
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function V4SettingsPage() {
  const { token }                      = useContext(AuthContext);
  const { grow_setup, setGrowSetup }   = useGrowSetup();
  const navigate                       = useNavigate();
  const base                           = useBasePath();

  const [setupName,     setSetupName]     = useState('');
  const [editingName,   setEditingName]   = useState(false);
  const [nameDraft,     setNameDraft]     = useState('');
  const [klima,         setKlima]         = useState(null);
  const [hydro,         setHydro]         = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [savingName,    setSavingName]    = useState(false);
  const [wizardOpen,    setWizardOpen]    = useState(false);
  const [deleteOpen,    setDeleteOpen]    = useState(false);
  const [snack,         setSnack]         = useState({ open: false, msg: '', sev: 'success' });

  const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' };

  const load = () => {
    if (!grow_setup) return;
    setLoading(true);
    Promise.all([
      axios.post(`/get_grow_setup_settings/${grow_setup}`, {}, { headers }),
      axios.get('/api/v4/devices', { params: { grow_setup_id: grow_setup }, headers }),
    ])
      .then(([settingsRes, devicesRes]) => {
        setSetupName(settingsRes.data?.name ?? 'Unbenannt');
        const devices = devicesRes.data?.devices ?? [];
        setKlima(devices.find(d => d.device_type === 1) ?? null);
        setHydro(devices.find(d => d.device_type === 2) ?? null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [grow_setup]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveName = async () => {
    if (!nameDraft.trim() || nameDraft === setupName) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const res = await axios.post(`/grow_setup_change_name/${grow_setup}`, { data: nameDraft }, { headers });
      if (res.data === 'Name already taken') {
        setSnack({ open: true, msg: 'Dieser Name wird schon verwendet', sev: 'error' });
        return;
      }
      setSetupName(nameDraft);
      setEditingName(false);
      setSnack({ open: true, msg: 'Name gespeichert', sev: 'success' });
    } catch { setSnack({ open: true, msg: 'Fehler beim Speichern', sev: 'error' }); }
    finally { setSavingName(false); }
  };

  const handleRemoveDevice = async (deviceObj, label) => {
    if (!deviceObj) return;
    try {
      await axios.post('/remove_device', { device_id: deviceObj.device_id }, { headers });
      setSnack({ open: true, msg: `${label} getrennt`, sev: 'info' });
      load();
    } catch { setSnack({ open: true, msg: 'Fehler beim Trennen', sev: 'error' }); }
  };

  const handleDeleteSetup = async () => {
    setDeleteOpen(false);
    try {
      await axios.post('/delete_grow_setup', { data: grow_setup }, { headers });
      setSnack({ open: true, msg: 'Grow Setup gelöscht', sev: 'success' });
      navigate(`${base}/dashboard`);
    } catch { setSnack({ open: true, msg: 'Fehler beim Löschen', sev: 'error' }); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress size={32} sx={{ color: 'var(--v4-cyan)' }} />
      </Box>
    );
  }

  return (
    <Box>
      <style>{ANIM_CSS}</style>

      {/* ── Setup name ── */}
      <SectionLabel>Grow-Setup</SectionLabel>
      <GlassCard sx={{ mb: 4, animation: 'v4FadeUp 0.4s both' }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" gap={1}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(var(--v4-cyan-rgb),0.5)', mb: 0.75 }}>
              Name
            </Typography>
            {editingName ? (
              <Box
                component="input"
                autoFocus
                value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                sx={{
                  width: '100%', py: 0.9, px: 1.5,
                  fontFamily: 'inherit', fontSize: '1.05rem', fontWeight: 700,
                  color: 'var(--v4-text)', background: 'rgba(var(--v4-cyan-rgb),0.06)',
                  border: '1px solid rgba(var(--v4-cyan-rgb),0.3)',
                  borderRadius: '10px', outline: 'none',
                  '&:focus': { borderColor: 'var(--v4-cyan)' },
                }}
              />
            ) : (
              <Typography
                sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--v4-text)', cursor: 'text' }}
                onClick={() => { setNameDraft(setupName); setEditingName(true); }}
              >
                {setupName}
              </Typography>
            )}
          </Box>

          {editingName ? (
            <Box onClick={handleSaveName} sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 1,
              borderRadius: '12px', cursor: 'pointer',
              background: 'rgba(var(--v4-cyan-rgb),0.12)', border: '1px solid rgba(var(--v4-cyan-rgb),0.35)',
              color: 'var(--v4-cyan)', fontWeight: 700, fontSize: '0.8rem',
              transition: 'all 0.16s',
              '&:hover': { background: 'rgba(var(--v4-cyan-rgb),0.2)' },
            }}>
              {savingName ? <CircularProgress size={14} sx={{ color: 'var(--v4-cyan)' }} /> : <SaveRoundedIcon sx={{ fontSize: 16 }} />}
              Speichern
            </Box>
          ) : (
            <Box onClick={() => { setNameDraft(setupName); setEditingName(true); }} sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 1,
              borderRadius: '12px', cursor: 'pointer',
              background: 'rgba(var(--v4-fg-rgb),0.04)', border: '1px solid rgba(var(--v4-fg-rgb),0.08)',
              color: 'rgba(var(--v4-muted-rgb),0.4)', fontWeight: 700, fontSize: '0.8rem',
              transition: 'all 0.16s',
              '&:hover': { background: 'rgba(var(--v4-fg-rgb),0.08)', color: 'var(--v4-text)' },
            }}>
              <EditRoundedIcon sx={{ fontSize: 16 }} /> Umbenennen
            </Box>
          )}
        </Stack>
      </GlassCard>

      {/* ── Devices ── */}
      <SectionLabel>Verbundene Geräte</SectionLabel>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <DeviceCard
            metaKey="klima"
            attachedDevice={klima}
            onAdd={() => setWizardOpen(true)}
            onRemove={() => handleRemoveDevice(klima, 'Klima-Kontroller')}
            animDelay={0}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DeviceCard
            metaKey="hydro"
            attachedDevice={hydro}
            onAdd={() => setWizardOpen(true)}
            onRemove={() => handleRemoveDevice(hydro, 'Hydro-Kontroller')}
            animDelay={80}
          />
        </Grid>
      </Grid>

      {/* ── Danger zone ── */}
      <SectionLabel>Gefahrenzone</SectionLabel>
      <GlassCard sx={{
        animation: 'v4FadeUp 0.5s 160ms both',
        border: '1px solid rgba(var(--v4-red-rgb),0.15)',
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: 'var(--v4-text)', fontSize: '0.92rem', mb: 0.4 }}>
              Grow Setup löschen
            </Typography>
            <Typography sx={{ fontSize: '0.73rem', color: 'rgba(var(--v4-muted-rgb),0.35)', lineHeight: 1.5 }}>
              Entfernt dieses Grow Setup und alle Gerätezuordnungen dauerhaft.
            </Typography>
          </Box>
          <Box onClick={() => setDeleteOpen(true)} sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, px: 2.5, py: 1.1,
            borderRadius: '12px', cursor: 'pointer',
            background: 'rgba(var(--v4-red-rgb),0.08)', border: '1px solid rgba(var(--v4-red-rgb),0.3)',
            color: 'var(--v4-red)', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
            transition: 'all 0.18s',
            '&:hover': { background: 'rgba(var(--v4-red-rgb),0.18)', boxShadow: '0 4px 18px rgba(var(--v4-red-rgb),0.2)' },
          }}>
            <DeleteForeverRoundedIcon sx={{ fontSize: 16 }} /> Löschen
          </Box>
        </Stack>
      </GlassCard>

      {/* ── Wizard ── */}
      <DeviceWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={() => { setWizardOpen(false); load(); }}
      />

      {/* ── Delete confirm overlay ── */}
      <DeleteConfirm
        open={deleteOpen}
        onConfirm={handleDeleteSetup}
        onCancel={() => setDeleteOpen(false)}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} variant="filled"
          onClose={() => setSnack(p => ({ ...p, open: false }))}
          sx={{ borderRadius: '14px' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
